import {UpmEntityType} from '@hconnect/common/types'
import {dataTestId, Loader} from '@hconnect/uikit'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useAssetsQuery} from '../../common/hooks'
import {useMaterialStorageListQuery} from '../../storages/hooks'
import {useCrusherDeletionValidation} from '../hooks/plantSetup/useCrusherDeletionValidation'
import {useDeleteDepartment} from '../hooks/upmEntityConfig/useDeleteDepartment'
import {useItemDetails} from '../providers'
import {useSelectedItem} from '../providers/SelectedItemProvider'

import {useGetSignals} from '@settings/modules/upm/components/SignalList/api/useGetSignals'

interface DeleteDepartmentContentProps {
  isCrusherBlocked: boolean
  assetsOrSignalsAttached: boolean
  sourceSystemCode: string | undefined
  isDeletedInSource: boolean | undefined
  departmentText: string | undefined
}

const DeleteDepartmentContent = ({
  isCrusherBlocked,
  assetsOrSignalsAttached,
  sourceSystemCode,
  isDeletedInSource,
  departmentText
}: DeleteDepartmentContentProps) => {
  const {t} = useTranslation()

  if (isCrusherBlocked) {
    return (
      <Typography>
        {t('plantSetup.departmentConfig.crusherUsedInCombination', {crusherName: departmentText})}
      </Typography>
    )
  }

  return (
    <>
      {assetsOrSignalsAttached && (
        <Typography>{t('plantSetup.departmentConfig.assetsOrStoragesMappedDepartment')}</Typography>
      )}
      {sourceSystemCode && !isDeletedInSource ? (
        <Typography fontWeight={'bold'}>
          {t('plantSetup.departmentConfig.departmentImportedFromSAP')}
        </Typography>
      ) : (
        <Typography>
          {t('plantSetup.departmentConfig.deleteDepartmentConfirmation', {departmentText})}
        </Typography>
      )}
    </>
  )
}

export const DeleteDepartmentPopup = () => {
  const {setDepartmentToDelete, departmentToDelete, departmentToDeleteParentUpmId} =
    useItemDetails()
  const {upmPlantId} = useSelectedItem()

  const isCrusher = departmentToDelete?.type?.name === 'Crusher'

  const {
    isUsedInCombination,
    isLoading: isLoadingCrusherValidation,
    isError: isCrusherValidationError
  } = useCrusherDeletionValidation(
    upmPlantId,
    isCrusher ? departmentToDeleteParentUpmId : undefined,
    isCrusher ? departmentToDelete?.upmId : undefined
  )

  const {data: assets, isLoading} = useAssetsQuery()
  const {data: materialStorages} = useMaterialStorageListQuery()
  const {data: signals, isLoading: isLoadingSignals} = useGetSignals({
    entityType: UpmEntityType.Department,
    entityId: departmentToDelete?.upmId ?? ''
  })

  const assetsOrSignalsAttached =
    assets?.some((a) => a.upmId === departmentToDelete?.upmId) ||
    materialStorages?.some((s) => s.upmId === departmentToDelete?.upmId) ||
    !!signals?.length

  const sourceSystemCode = departmentToDelete?.sourceSystemCode
  const isDeletedInSource = departmentToDelete?.isDeletedInSource
  const departmentText = departmentToDelete?.text

  const {mutate: deleteDepartment} = useDeleteDepartment()
  const {t} = useTranslation()

  const handleClose = () => setDepartmentToDelete()

  const handleDeleteDepartment = () => {
    if (departmentToDelete) {
      deleteDepartment(departmentToDelete.upmId, {onSuccess: handleClose})
    }
  }

  if (isLoading || isLoadingSignals || isLoadingCrusherValidation) {
    return <Loader />
  }

  const isCrusherBlocked = isCrusher && (isUsedInCombination || isCrusherValidationError)
  const canDelete = !isCrusherBlocked && (isDeletedInSource || !sourceSystemCode)

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('plantSetup.departmentConfig.deleteDepartment')}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <DeleteDepartmentContent
            isCrusherBlocked={isCrusherBlocked}
            assetsOrSignalsAttached={!!assetsOrSignalsAttached}
            sourceSystemCode={sourceSystemCode}
            isDeletedInSource={isDeletedInSource}
            departmentText={departmentText}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {isDeletedInSource ? t('common.cancel') : t('common.close')}
        </Button>
        {canDelete ? (
          <Button
            {...dataTestId('delete-department-btn')}
            type="submit"
            variant="contained"
            color="error"
            onClick={handleDeleteDepartment}
          >
            {t('common.remove')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
